import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { installations, installationTypes, Installation } from "@/mocks/installations";
import BeforeAfterSection from "@/pages/products/components/BeforeAfterSection";

const typeColors: Record<string, string> = {
  "아파트": "bg-emerald-100 text-emerald-700",
  "오피스텔": "bg-sky-100 text-sky-700",
  "단독주택": "bg-amber-100 text-amber-700",
  "빌라": "bg-violet-100 text-violet-700",
  "사무실": "bg-rose-100 text-rose-700",
  "주상복합": "bg-orange-100 text-orange-700",
  "아파트 단지": "bg-teal-100 text-teal-700",
  "상업시설": "bg-pink-100 text-pink-700",
};

interface ModalProps {
  item: Installation;
  onClose: () => void;
}

const InstallationModal = ({ item, onClose }: ModalProps) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
    <div
      className="relative bg-white rounded-2xl overflow-hidden max-w-2xl w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full overflow-hidden" style={{ height: "280px" }}>
        <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
      </div>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white transition-colors"
      >
        <i className="ri-close-line text-stone-700" />
      </button>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${typeColors[item.type] || "bg-stone-100 text-stone-600"}`}>
            {item.type}
          </span>
          <span className="text-xs text-stone-400">{item.date}</span>
        </div>
        <h3 className="text-lg font-black text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
        <p className="text-xs text-stone-500 mb-1">
          <i className="ri-map-pin-line mr-1" />{item.location}
        </p>
        <p className="text-xs font-semibold text-stone-600 mb-4">
          <i className="ri-lock-line mr-1" />{item.product}
        </p>
        <div className="bg-stone-50 rounded-xl p-4">
          <div className="flex items-center gap-1 mb-2">
            {Array.from({ length: item.rating }).map((_, i) => (
              <div key={i} className="w-3 h-3 flex items-center justify-center">
                <i className="ri-star-fill text-amber-400 text-xs" />
              </div>
            ))}
          </div>
          <p className="text-sm text-stone-700 leading-relaxed italic">&ldquo;{item.review}&rdquo;</p>
          <p className="text-xs font-semibold text-stone-500 mt-3">— {item.reviewer}</p>
        </div>
      </div>
    </div>
  </div>
);

const ProductsPage = () => {
  const [selectedType, setSelectedType] = useState("전체");
  const [selectedItem, setSelectedItem] = useState<Installation | null>(null);

  const filtered = selectedType === "전체"
    ? installations
    : installations.filter((i) => i.type === selectedType);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-20">
        {/* Hero Banner */}
        <section className="relative mb-16 overflow-hidden" style={{ height: "420px" }}>
          <img
            src="https://public.readdy.ai/ai/img_res/b9046c05dfad122b0cb49f8849548927.jpg"
            alt="설치사례 히어로"
            className="w-full h-full object-cover object-top"
          />
          {/* 다층 오버레이 */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          <div className="absolute inset-0 flex flex-col justify-end max-w-7xl mx-auto px-6 md:px-10 pb-12">
            {/* Top label */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-px bg-white/40" />
              <p className="text-[10px] font-bold tracking-widest uppercase text-white/50">Installation Cases</p>
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <h1 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                  설치사례
                </h1>
                <p className="text-sm text-white/60 max-w-xs leading-relaxed">
                  전국 15,000+ 현장에서 검증된<br />도어준의 프리미엄 설치 사례
                </p>
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-6 md:gap-8">
                {[
                  { value: "15,000+", label: "설치 완료" },
                  { value: "4.9점", label: "평균 만족도" },
                  { value: "당일", label: "설치 완료" },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                    <p className="text-[10px] text-white/50 mt-0.5 tracking-wide">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { icon: "ri-home-4-line", value: "15,000+", label: "총 설치 건수" },
              { icon: "ri-map-pin-line", value: "전국 17개", label: "서비스 지역" },
              { icon: "ri-star-fill", value: "4.9점", label: "평균 만족도" },
              { icon: "ri-time-line", value: "당일", label: "설치 완료" },
            ].map((s) => (
              <div key={s.label} className="bg-stone-50 rounded-xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                  <i className={`${s.icon} text-stone-700 text-lg`} />
                </div>
                <div>
                  <p className="text-lg font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-xs text-stone-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Before & After Section */}
        <BeforeAfterSection />

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Filter */}
          <div className="flex items-center gap-2 flex-wrap mb-8 mt-12">
            {installationTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  selectedType === type
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <p className="text-xs text-stone-400 mb-6">{filtered.length}개 사례</p>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="group bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelectedItem(item)}
              >
                {/* Image */}
                <div className="relative overflow-hidden" style={{ height: "220px" }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${typeColors[item.type] || "bg-stone-100 text-stone-600"}`}>
                      {item.type}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full px-4 py-2 text-xs font-semibold text-stone-800">
                      자세히 보기
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-stone-400">
                      <i className="ri-map-pin-line mr-1" />{item.location}
                    </p>
                    <p className="text-xs text-stone-400">{item.date}</p>
                  </div>
                  <h3 className="text-sm font-bold text-stone-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-stone-500 mb-3">
                    <i className="ri-lock-line mr-1" />{item.product}
                  </p>

                  {/* Review Preview */}
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="flex items-center gap-1 mb-1.5">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-xs" />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-stone-600 leading-relaxed line-clamp-2 italic">
                      &ldquo;{item.review}&rdquo;
                    </p>
                    <p className="text-[10px] font-semibold text-stone-400 mt-2">— {item.reviewer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-stone-900 rounded-2xl p-10 text-center">
            <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              설치 문의하기
            </h2>
            <p className="text-sm text-stone-400 mb-6">전국 어디서나 당일 설치 가능합니다. 지금 바로 문의하세요.</p>
            <a
              href="tel:01097939490"
              className="inline-flex items-center gap-2 bg-white text-stone-900 text-sm font-bold px-8 py-3 rounded-full hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              010-9793-9490 전화 상담
            </a>
          </div>
        </div>
      </main>

      {selectedItem && (
        <InstallationModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}

      <Footer />
    </div>
  );
};

export default ProductsPage;
